<template>
  <header class="navbar" :class="navClass">
    <div class="container">
      <nav>

        <LogoBox/>

        <div class="lg:hidden flex items-center ms-auto px-2.5">
          <button
              class="hs-collapse-toggle inline-flex items-center justify-center h-9 w-12 rounded-md border border-white/20 bg-default-100/5"
              type="button" id="hs-unstyled-collapse" data-hs-collapse="#mobileMenu" data-hs-type="collapse"
          >
            <Menu class="h-5 w-5 stroke-white"/>
          </button>
        </div>

        <div id="mobileMenu"
             class="hs-collapse transition-all duration-300 lg:basis-auto basis-full grow hidden lg:flex items-center justify-center mx-auto mt-2 lg:mt-0"
        >
          <ul id="navbar-navlist" class="navbar-nav">
            <li v-for="(link,idx) in navLinks" :key="link.id"
                class="nav-item"
                :class="{ 'active': isActiveRoute(link.route.url)} ">
              <!--|| activeSectionIndex === idx }
              {{ isActiveRoute(link.route.url)}} {{link.route.url }} {{ isActiveRoute(link.route.url)}} {{  activeSectionIndex === idx}}
              -->
              <router-link
                :to=" link.route.url ? link.route.url : link.route"
                class="nav-link"
                @click="closeMenu"
              >
                {{ link.label }}
              </router-link>
            </li>
          </ul>

          <div v-if="authStore.isAuthenticated" class="lg:hidden flex items-center pt-4 mt-4 lg:pt-0 lg:mt-0 border-t border-white/10 space-x-4 lg:border-none">
            <router-link
              to="/my/home"
              class="inline-flex items-center justify-center gap-2 bg-primary text-white py-2 px-5 rounded-lg hover:bg-primary-hover transition-all duration-300 shadow-md"
              @click="closeMenu"
            >
              <PodcastIcon class="h-5 w-5 me-2"/>
              Voice Area
            </router-link>
            <!-- Logout Button -->
            <button
              @click="authStore.logout(); closeMenu();"
              class="inline-flex items-center justify-center gap-2 bg-red-900 text-white py-2 px-5 rounded-lg
              hover:bg-red-900/80 transition-all duration-300 shadow-md"
            >
              <LogOutIcon class="h-5 w-5 me-2"/>
              Logout
            </button>
          </div>
          <div v-else class="lg:hidden flex items-center pt-4 mt-4 lg:pt-0 lg:mt-0 border-t border-white/10 space-x-4 lg:border-none">
            <!-- Sign In Button -->
            <router-link
              to="/auth/sign-in"
              class="inline-flex items-center justify-center gap-2 bg-primary/90 text-white py-2 px-5 rounded-lg
              hover:bg-primary-hover transition-all duration-300 shadow-md"
              @click="closeMenu"
            >
              <PodcastIcon class="h-5 w-5 me-2"/>
              Sign In
            </router-link>

            <!-- Sign Up Button -->
            <router-link
              to="/auth/sign-up"
              class="inline-flex items-center justify-center gap-2 bg-primary/90 text-white py-2 px-5 rounded-lg
              hover:bg-primary-hover transition-all duration-300 shadow-md"
              @click="closeMenu"
            >
              <LucideMicVocal class="h-5 w-5 me-2" />
              Sign Up
            </router-link>
          </div>
        </div>

        <div v-if="authStore.isAuthenticated" class="hidden lg:flex items-center space-x-4">
          <router-link to="/my/home"
                  class="inline-flex items-center justify-center gap-2 bg-primary text-white py-2 px-5 rounded-lg
              hover:bg-primary-hover transition-all duration-300 shadow-md">
            <PodcastIcon class="h-5 w-5 me-2"/>
            Voice Area
          </router-link>
          <!-- Logout Button -->
          <button @click="authStore.logout()"
             class="inline-flex items-center justify-center gap-2 bg-red-900 text-white py-2 px-5 rounded-lg
              hover:bg-red-900/80 transition-all duration-300 shadow-md">
            <LogOutIcon class="h-5 w-5 me-2"/>
            Logout
          </button>
        </div>
        <div v-else class="hidden lg:flex items-center space-x-4">
          <!-- Sign In Button -->
          <router-link
            to="/auth/sign-in"
            class="inline-flex items-center justify-center gap-2 bg-primary/90 text-white py-2 px-5 rounded-lg
              hover:bg-primary-hover transition-all duration-300 shadow-md"
          >
            <PodcastIcon class="h-5 w-5 me-2"/>
            Sign In
          </router-link>

          <!-- Sign Up Button -->
          <router-link
            to="/auth/sign-up"
            class="inline-flex items-center justify-center gap-2 bg-primary/90 text-white py-2 px-5 rounded-lg
              hover:bg-primary-hover transition-all duration-300 shadow-md"
          >
            <LucideMicVocal class="h-5 w-5 me-2" />
            Sign Up
          </router-link>

        </div>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, type PropType, ref} from "vue";
import type {NavBarLinkType} from "@/types/layout";
import LogoBox from "@/components/LogoBox.vue";
import { useAuthStore } from '@/stores/auth';
import {ArrowBigDownDash, LucideMicVocal, PodcastIcon, Menu, LogOutIcon} from 'lucide-vue-next';
import {useRoute} from "vue-router";

const props = defineProps({
  navLinks: {
    type: Array as PropType<NavBarLinkType[]>,
    required: true
  }
})

const authStore = useAuthStore();

const navClass = ref<string>('')
const route = useRoute(); // Access Vue Router's current route



const closeMenu = () => {
  if(window.innerWidth < 1024) {
    const {element} = window.HSCollapse.getInstance('#hs-unstyled-collapse', true);
    if (element) {
      //window.HSCollapse.hide('#hs-unstyled-collapse');
      element.hide()
    } else {
      log.error('Element for hs collapse not found');
    }
  }
};


onMounted(() => {
  window.addEventListener("scroll", () => {
    if (document.body.scrollTop >= 50 || document.documentElement.scrollTop >= 50) {
      navClass.value = 'nav-sticky'
    } else {
      navClass.value = ''
    }
  });

  window.addEventListener("scroll", getActiveSectionIndex);
})

onUnmounted(() => {
  window.removeEventListener("scroll", getActiveSectionIndex);
})

let activeSectionIndex = ref(0)

// Function to check if the route is active
const isActiveRoute = (routeUrl: string) => {
  return route.path.startsWith(routeUrl);
};
const getActiveSectionIndex = () => {
  const currentScrollPos = window.pageYOffset;

  for (let i = 0; i < props.navLinks.length; i++) {
    const section = document.getElementById(props.navLinks[i].id);
    if (section) {
      const sectionTop = section.offsetTop - 70;
      const sectionHeight = section.offsetHeight;
      if (
          currentScrollPos >= sectionTop &&
          currentScrollPos < sectionTop + sectionHeight
      ) {
        activeSectionIndex.value = i;
        break;
      }
    }
  }
}
</script>
